import { getDepictRecommendedProducts } from "@/actions/products/get-depict-recommended-products"
import { depictMerchant } from "@/data/depict-merchant"
import { transformDepictDisplayResponse } from "@/lib/depict-client/helpers/transformDepictResponse"
import useDepictClientMarketAndLocaleCode from "@/lib/depict-client/helpers/useDepictClientMarketAndLocaleCode"
import useDepictClientSessionId from "@/lib/depict-client/helpers/useDepictClientSessionId"
import logger from "@/lib/logger/logger-init"
import { MinimizedCentraProduct } from "@/types/centra/checkout-api/products/post-products"
import { useEffect, useState } from "react"

type UseFetchRecommendedProductsParams = {
    productIds: string[]
    isLoading?: boolean
    limit?: number
    depictType: string
}

type UseFetchRecommendedProductsResult = {
    groupedProductsRecord: Record<string, MinimizedCentraProduct[]> | null
    loading: boolean
    groupedResultCount: number
}

export const useFetchRecommendedProducts = ({
    productIds,
    isLoading = false,
    limit = 9,
    depictType
}: UseFetchRecommendedProductsParams): UseFetchRecommendedProductsResult => {
    const [groupedProductsRecord, setGroupedProductsRecord] = useState<Record<
        string,
        MinimizedCentraProduct[]
    > | null>(null)
    const { marketCode, localeCode } = useDepictClientMarketAndLocaleCode()
    const [loading, setLoading] = useState(false)
    const [groupedResultCount, setGroupedResultCount] = useState(0)
    const depictSessionId = useDepictClientSessionId()

    useEffect(() => {
        // safeguard against infinite loop

        const fetchRecommendedProducts = async () => {
            setLoading(true)
            const product_ids = productIds ? { product_ids: productIds } : undefined

            try {
                const request = await getDepictRecommendedProducts({
                    market: marketCode,
                    locale: localeCode,
                    merchant: depictMerchant,
                    type: depictType,
                    limit: limit,
                    ...product_ids,
                    session_id: depictSessionId
                })

                const groupedProducts = transformDepictDisplayResponse(
                    request,
                    "recommendation_id"
                )

                const groupedProductsRecord = groupedProducts.reduce(
                    (acc, { key, items }) => {
                        acc[key] = items
                        return acc
                    },
                    {} as Record<string, MinimizedCentraProduct[]>
                )

                setGroupedResultCount(Object.keys(groupedProductsRecord).length)
                setGroupedProductsRecord(groupedProductsRecord)
                setLoading(false)
            } catch (error) {
                logger.error("Failed to fetch recommended products")
                setLoading(false)
            } finally {
                setLoading(false)
            }
        }

        fetchRecommendedProducts()
    }, [productIds, isLoading, localeCode, marketCode])

    return {
        groupedProductsRecord,
        loading,
        groupedResultCount
    }
}
